// Стрелки и Enter в колонке списка.
//
// ## Зачем
//
// Списки в кабинете длинные: 286 человек, 281 строка ведомости, очередь заявок. Дойти до
// строки можно было только мышью — Tab уводит по всем ссылкам подряд, включая полосу разделов
// и отборы, то есть до третьей фамилии от поля поиска двенадцать нажатий.
//
// Привычка, которую человек приносит из почты и мессенджера, одна: стрелка вниз идёт по
// строкам, Enter открывает. Она работает и на клавиатуре, и на цифровом блоке, и её не надо
// объяснять.
//
// ## Откуда начинается ход
//
// Из поля поиска. Набрал фамилию, нажал стрелку вниз — попал на первую строку, Enter — открыл.
// Ради этого обработчик висит на ВСЕЙ колонке, а не на списке: событие всплывает из поля.
//
// Если ничего не в фокусе, ход начинается от ВЫБРАННОЙ строки (`aria-current`), а не от
// первой. Человек вернулся в список с карточки — стрелка вниз обязана дать соседа, а не
// увезти его в начало из 286 строк.
//
// ## Где стрелки НЕ наши
//
// Внутри выпадающего списка, поля со списком и многострочного поля стрелки принадлежат им:
// там ими выбирают пункт и двигают каретку. Перехват сломал бы отбор по юрлицу — самый
// частый выпадающий список кабинета.

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useCallback, useRef } from 'react';

/** Признак строки списка. Ставит его `ListRow`; по нему же строки и ищутся. */
export const ПРИЗНАК_СТРОКИ = 'data-строка-списка';

/** Чужие стрелки: внутри этого перехватывать нельзя. */
const ЧУЖИЕ = '[role="combobox"], [role="listbox"], [role="menu"], select, textarea, [contenteditable="true"]';

export function useКлавиатураСписка() {
  const колонка = useRef<HTMLElement>(null);

  const onKeyDown = useCallback((событие: ReactKeyboardEvent<HTMLElement>) => {
    const шаг =
      событие.key === 'ArrowDown'
        ? 1
        : событие.key === 'ArrowUp'
          ? -1
          : событие.key === 'Home'
            ? 'начало'
            : событие.key === 'End'
              ? 'конец'
              : null;
    if (шаг === null || событие.altKey || событие.metaKey || событие.ctrlKey) {
      return;
    }
    const откуда = событие.target;
    if (откуда instanceof Element && откуда.closest(ЧУЖИЕ) !== null) {
      return;
    }
    const корень = колонка.current;
    if (корень === null) {
      return;
    }
    const строки = [...корень.querySelectorAll<HTMLElement>(`[${ПРИЗНАК_СТРОКИ}]`)];
    if (строки.length === 0) {
      return;
    }

    const вФокусе = строки.indexOf(document.activeElement as HTMLElement);
    // Открытая строка — точка отсчёта, когда фокуса в списке нет. Возврат с карточки должен
    // давать соседа, а не начало списка.
    const открытая = строки.findIndex((строка) => строка.getAttribute('aria-current') === 'true');
    const сейчас = вФокусе >= 0 ? вФокусе : открытая;

    let следующий: number;
    if (шаг === 'начало') {
      следующий = 0;
    } else if (шаг === 'конец') {
      следующий = строки.length - 1;
    } else if (сейчас < 0) {
      // Ход из поля поиска: вниз — первая строка, вверх — последняя.
      следующий = шаг === 1 ? 0 : строки.length - 1;
    } else {
      следующий = сейчас + шаг;
    }
    // По краям стоим, а не заворачиваемся: заворот на длинном списке человек читает как
    // прыжок неизвестно куда — он не видел, что дошёл до конца.
    if (следующий < 0 || следующий >= строки.length) {
      return;
    }

    событие.preventDefault();
    const цель = строки[следующий];
    цель?.focus();
    цель?.scrollIntoView({ block: 'nearest' });
  }, []);

  return { колонка, onKeyDown };
}
